/**
 * ContactPicker - Reusable modal for searching and selecting contacts
 * 
 * A standalone component that displays a modal with BooleanSearch and DataGrid
 * for searching and selecting contacts with a Promise-based API.
 * 
 * Features:
 * - BooleanSearch with contact-specific fields built from Standing Data
 * - DataGrid with server-side pagination
 * - Saved searches support
 * - Remembers last search per user
 * - Exclude specific contact IDs from results
 * 
 * Usage:
 * ```typescript
 * import { ContactPicker } from '@/components/ContactPicker';
 * 
 * // Simple usage
 * const result = await ContactPicker.show();
 * if (result.selected) {
 *   console.log('Selected:', result.contactId, result.fullName);
 * }
 * 
 * // With options
 * const result = await ContactPicker.show({
 *   title: 'Select Related Contact',
 *   excludeContactId: 123, // Exclude current contact
 * });
 * ```
 */

import { BooleanSearch, type BooleanSearchField, type SearchTreeJson } from '@/components/BooleanSearch';
import { DataGrid, type DataGridColumn } from '@/components/DataGrid';
import { ModalSystem } from '@/services/ModalSystem';
import { ToasterService } from '@/services/ToasterService';
import { Logger } from '@/services/Logger';
import { userSettingsService, SettingScope } from '@/services/UserSettingsService';
import { trisysAPI } from '@/services/api/TriSysWebAPI';

const logger = Logger.scope('ContactPicker');

// ============================================================================
// Types
// ============================================================================

export interface ContactPickerOptions {
  /** Modal title (default: 'Select Contact') */
  title?: string;
  
  /** Contact ID to exclude from results (e.g., current contact being edited) */
  excludeContactId?: number;
  
  /** Additional contact IDs to exclude */
  excludeContactIds?: number[];
  
  /** Custom page size for results (default: 10) */
  pageSize?: number;
  
  /** Modal width class (default: 'w-[900px] max-w-[95vw]') */
  modalWidth?: string;
  
  /** API service to use for searching contacts (injectable for tenant-specific APIs) */
  apiService?: ContactPickerApiService;
}

export interface ContactPickerResult {
  /** Whether a contact was selected (true) or cancelled (false) */
  selected: boolean;
  
  /** Selected contact ID (if selected) */
  contactId?: number;
  
  /** Selected contact name (if selected) */
  fullName?: string;
  
  /** Full record data (if selected) */
  record?: Record<string, unknown>;
}

/**
 * Interface for the API service that ContactPicker uses
 * This allows different tenants to provide their own search implementation
 */
export interface ContactPickerApiService {
  /** Search contacts with optional boolean search tree */
  searchContactsWithTree(params: {
    PageNumber: number;
    RecordsPerPage: number;
    SearchTreeEnabled?: boolean;
    SearchTreeJson?: string;
  }): Promise<{
    List: Record<string, unknown>[];
    TotalRecordCount: number;
  }>;
}

// Setting key for persisting last search
const LAST_SEARCH_SETTING_KEY = 'search.contactPicker.lastTree';

// ============================================================================
// ContactPicker Component
// ============================================================================

/**
 * ContactPicker - Static modal for searching and selecting contacts
 */
export class ContactPicker {
  private options: Required<Omit<ContactPickerOptions, 'excludeContactId' | 'excludeContactIds' | 'apiService'>> & 
    Pick<ContactPickerOptions, 'excludeContactId' | 'excludeContactIds' | 'apiService'>;
  
  private modalId: string | null = null;
  private dataGrid: DataGrid | null = null;
  private booleanSearch: BooleanSearch | null = null;
  private searchTree: SearchTreeJson | null = null;
  private currentPage: number = 1;
  private pageSize: number;
  
  private resolvePromise: ((result: ContactPickerResult) => void) | null = null;
  
  private constructor(options: ContactPickerOptions = {}) {
    this.options = {
      title: options.title ?? 'Select Contact',
      pageSize: options.pageSize ?? 10,
      modalWidth: options.modalWidth ?? 'w-[900px] max-w-[95vw]',
      excludeContactId: options.excludeContactId,
      excludeContactIds: options.excludeContactIds,
      apiService: options.apiService
    };
    this.pageSize = this.options.pageSize;
  }
  
  /**
   * Show the contact picker modal and return a Promise with the result
   * 
   * @param options - Configuration options
   * @returns Promise resolving to the picker result
   * 
   * @example
   * ```typescript
   * const result = await ContactPicker.show({
   *   excludeContactId: currentContactId
   * });
   * 
   * if (result.selected) {
   *   console.log('Selected contact:', result.contactId, result.fullName);
   * }
   * ```
   */
  static show(options: ContactPickerOptions = {}): Promise<ContactPickerResult> {
    const picker = new ContactPicker(options);
    return picker.showModal();
  }
  
  private showModal(): Promise<ContactPickerResult> {
    return new Promise((resolve) => {
      this.resolvePromise = resolve;
      
      // Reset state
      this.currentPage = 1;
      this.searchTree = null;
      
      const modalHtml = this.buildModalHtml();
      const modalTitle = this.buildModalTitle();
      
      this.modalId = ModalSystem.show({
        title: modalTitle,
        html: modalHtml,
        width: this.options.modalWidth,
        buttons: [
          {
            caption: 'Cancel',
            className: 'btn-ghost',
            callback: () => {
              this.handleCancel();
              return true;
            }
          }
        ],
        onOpened: () => this.initializeComponents(),
        onClosed: () => this.cleanup()
      });
    });
  }
  
  private buildModalHtml(): string {
    return `
      <div class="flex flex-col" style="height: 70vh; max-height: 700px; min-height: 500px;">
        <!-- Boolean Search Panel -->
        <div class="mb-3 p-3 bg-base-200 rounded-lg flex-shrink-0">
          <div id="contact-picker-search-container"></div>
        </div>
        
        <!-- Results Grid - flex-1 to fill remaining space -->
        <div class="flex-1 overflow-auto min-h-0" style="scrollbar-width: thin;">
          <div id="contact-picker-grid-container" class="h-full"></div>
        </div>
      </div>
    `;
  }
  
  private buildModalTitle(): string {
    return `
      <div class="flex items-center justify-between w-full">
        <div class="flex items-center">
          <i class="fas fa-user-search text-primary mr-2"></i>
          <span>${this.escapeHtml(this.options.title)}</span>
        </div>
        <details class="dropdown dropdown-end" id="contact-picker-saved-searches-details">
          <summary class="btn btn-xs btn-ghost m-0">
            <i class="fas fa-bookmark mr-1"></i> Saved Searches
            <i class="fas fa-chevron-down ml-1 text-xs"></i>
          </summary>
          <ul id="contact-picker-saved-searches-dropdown" class="dropdown-content flex flex-col p-2 shadow bg-base-100 rounded-box z-50 text-sm" style="max-height: 50vh; overflow-y: auto; overflow-x: hidden; width: max-content; min-width: 200px; scrollbar-width: thin;">
            <li class="list-none px-3 py-1 text-xs text-base-content/60 font-semibold">Your Searches</li>
            <li class="list-none"><span class="px-3 py-2 text-base-content/50 italic text-xs whitespace-nowrap">Loading...</span></li>
          </ul>
        </details>
      </div>
    `;
  }
  
  private async initializeComponents(): Promise<void> {
    try {
      // Load saved searches
      await this.loadSavedSearches();
      
      // Initialize Boolean Search
      await this.initBooleanSearch();
      
      // Initialize DataGrid
      await this.initDataGrid();
      
      // Try to restore last search
      await this.restoreLastSearch();
      
      logger.debug('Contact picker initialized');
    } catch (error) {
      logger.error('Failed to initialize contact picker', error);
      ToasterService.error('Failed to initialize contact search');
    }
  }
  
  // ============================================================================
  // Saved Searches
  // ============================================================================
  
  private async loadSavedSearches(): Promise<void> {
    const dropdown = document.getElementById('contact-picker-saved-searches-dropdown');
    if (!dropdown) return;
    
    try {
      const response = await trisysAPI.getSavedSearches('Contact', 1, 100);
      
      if (!response.Success) {
        logger.warn('Failed to load saved searches', { error: response.ErrorMessage });
        this.updateSavedSearchesDropdown([]);
        return;
      }
      
      // Filter to current user's searches
      const userProfile = await trisysAPI.getUserProfile();
      const currentUserName = userProfile?.displayName || '';
      
      const allSearches = response.List || [];
      const userSearches = allSearches.filter(search => search.UserName === currentUserName);
      
      logger.debug('Saved searches loaded', { total: allSearches.length, filtered: userSearches.length });
      
      this.updateSavedSearchesDropdown(userSearches);
    } catch (error) {
      logger.error('Error loading saved searches', { error });
      this.updateSavedSearchesDropdown([]);
    }
  }
  
  private updateSavedSearchesDropdown(searches: Array<{ ApexEntitySearchId: number; SearchName: string; DefaultSearch?: boolean }>): void {
    const dropdown = document.getElementById('contact-picker-saved-searches-dropdown');
    if (!dropdown) return;
    
    let html = `
      <li class="list-none px-3 py-1 text-xs text-base-content/60 font-semibold">Your Searches</li>
    `;
    
    if (searches.length === 0) {
      html += `<li class="list-none"><span class="px-3 py-2 text-base-content/50 italic text-xs whitespace-nowrap">No saved searches</span></li>`;
    } else {
      // Default searches first
      const defaultSearches = searches.filter(s => s.DefaultSearch);
      const otherSearches = searches.filter(s => !s.DefaultSearch);
      
      for (const search of defaultSearches) {
        html += `
          <li class="list-none">
            <a class="contact-picker-saved-search flex items-center gap-2 px-2 py-1.5 hover:bg-base-200 rounded cursor-pointer whitespace-nowrap text-sm" data-search-id="${search.ApexEntitySearchId}">
              <i class="fas fa-star text-warning text-xs"></i>
              <span>${this.escapeHtml(search.SearchName)}</span>
            </a>
          </li>
        `;
      }
      
      for (const search of otherSearches) {
        html += `
          <li class="list-none">
            <a class="contact-picker-saved-search flex items-center gap-2 px-2 py-1.5 hover:bg-base-200 rounded cursor-pointer whitespace-nowrap text-sm" data-search-id="${search.ApexEntitySearchId}">
              <i class="fas fa-search text-base-content/50 text-xs"></i>
              <span>${this.escapeHtml(search.SearchName)}</span>
            </a>
          </li>
        `;
      }
    }
    
    dropdown.innerHTML = html;
    
    // Bind click handlers
    const detailsEl = document.getElementById('contact-picker-saved-searches-details') as HTMLDetailsElement;
    const items = dropdown.querySelectorAll('.contact-picker-saved-search');
    items.forEach(item => {
      item.addEventListener('click', async (e) => {
        e.preventDefault();
        const searchId = parseInt((item as HTMLElement).dataset.searchId || '0', 10);
        if (searchId > 0) {
          if (detailsEl) detailsEl.open = false;
          await this.loadSavedSearch(searchId);
        }
      });
    });
  }
  
  private async loadSavedSearch(apexEntitySearchId: number): Promise<void> {
    try {
      logger.debug('Loading saved search', { apexEntitySearchId });
      
      const response = await trisysAPI.readSavedSearch('Contact', apexEntitySearchId);
      
      if (!response.Success || !response.Criteria) {
        ToasterService.warning('Could not load saved search');
        return;
      }
      
      const jsonCriteria = response.Criteria.JsonCriteria;
      if (!jsonCriteria) {
        ToasterService.warning('Saved search has no criteria');
        return;
      }
      
      if (this.booleanSearch) {
        const loaded = await this.booleanSearch.loadTree(jsonCriteria);
        
        if (loaded) {
          this.searchTree = this.booleanSearch.getSearchTree();
          this.currentPage = 1;
          await this.loadResults();
        }
      }
    } catch (error) {
      logger.error('Failed to load saved search', { error });
      ToasterService.error('Failed to load saved search');
    }
  }
  
  // ============================================================================
  // Last Search Persistence
  // ============================================================================
  
  private saveLastSearch(): void {
    if (!this.searchTree) return;
    
    try {
      const json = JSON.stringify(this.searchTree);
      userSettingsService.set(LAST_SEARCH_SETTING_KEY, json);
      logger.debug('Contact picker search tree persisted', { treeLength: json.length });
    } catch (error) {
      logger.warn('Failed to persist search tree', { error });
    }
  }
  
  private async restoreLastSearch(): Promise<boolean> {
    try {
      const savedValue = userSettingsService.get<string | null>(LAST_SEARCH_SETTING_KEY, null);
      
      logger.debug('Attempting to restore last search', { 
        hasJson: !!savedValue, 
        hasBooleanSearch: !!this.booleanSearch
      });
      
      if (!savedValue || !this.booleanSearch) {
        logger.debug('No saved search to restore');
        return false;
      }
      
      const searchTree = typeof savedValue === 'string' ? JSON.parse(savedValue) : savedValue;
      
      logger.debug('Parsed search tree', { 
        rulesCount: searchTree?.Tree?.rules?.length || searchTree?.rules?.length
      });
      
      const loaded = await this.booleanSearch.loadTree(searchTree);
      
      if (loaded) {
        this.searchTree = this.booleanSearch.getSearchTree();
        logger.info('Restored last search', { 
          rulesCount: searchTree?.Tree?.rules?.length || searchTree?.rules?.length
        });
        
        this.currentPage = 1;
        await this.loadResults();
        return true;
      } else {
        logger.warn('Failed to load saved search tree');
        await userSettingsService.set(LAST_SEARCH_SETTING_KEY, null);
        return false;
      }
    } catch (error) {
      logger.warn('Failed to restore search tree', { error });
      await userSettingsService.set(LAST_SEARCH_SETTING_KEY, null);
      return false;
    }
  }
  
  // ============================================================================
  // Boolean Search
  // ============================================================================
  
  private async initBooleanSearch(): Promise<void> {
    const container = document.getElementById('contact-picker-search-container');
    if (!container) {
      logger.warn('Boolean search container not found');
      return;
    }
    
    const standingData = trisysAPI.getCachedStandingData();
    const fields = this.buildSearchFields(standingData);
    
    this.booleanSearch = new BooleanSearch(container, {
      entityName: 'Contact',
      entitySearchId: 53,
      fields,
      standingData: standingData || undefined,
      showSearchButton: true,
      showClearButton: true,
      onSearch: (searchTree) => {
        this.searchTree = searchTree;
        this.currentPage = 1;
        this.loadResults();
      }
    });
    
    await this.booleanSearch.initialize();
    logger.debug('Boolean Search initialized');
  }
  
  private buildSearchFields(standingData: ReturnType<typeof trisysAPI.getCachedStandingData>): BooleanSearchField[] {
    if (!standingData?.FieldDescriptions) {
      logger.warn('Standing data not available, using minimal fields');
      return this.getDefaultFields();
    }
    
    const contactFields = standingData.FieldDescriptions.filter(fd => {
      const tableName = fd.TableName?.toLowerCase() || '';
      return tableName === 'contactconfigfields';
    });
    
    const fields: BooleanSearchField[] = contactFields.slice(0, 30).map(fd => {
      const fieldId = `${fd.TableName}_${fd.TableFieldName}`;
      
      const field: BooleanSearchField = {
        id: fieldId,
        label: fd.FieldLabel || fd.TableFieldName,
        type: this.mapFieldTypeId(fd.FieldTypeId),
        input: 'text'
      };
      
      const skillCategory = standingData.SkillCategories?.find(sc => sc.FieldId === fd.FieldId);
      if (skillCategory) {
        const skills = standingData.Skills?.filter(s => s.SkillCategoryId === skillCategory.SkillCategoryId) || [];
        if (skills.length > 0) {
          field.type = 'integer';
          field.values = skills.map(s => ({ 
            value: s.SkillId,
            label: s.Skill
          }));
          field.input = 'select';
          field.default_operator = 'in';
        }
      }
      
      return field;
    });
    
    // Sort with priority fields first
    const priorityFields = ['Forename', 'Surname', 'Full Name'];
    fields.sort((a, b) => {
      const aIndex = priorityFields.findIndex(p => a.label.toLowerCase() === p.toLowerCase());
      const bIndex = priorityFields.findIndex(p => b.label.toLowerCase() === p.toLowerCase());
      
      if (aIndex >= 0 && bIndex >= 0) return aIndex - bIndex;
      if (aIndex >= 0) return -1;
      if (bIndex >= 0) return 1;
      return a.label.localeCompare(b.label);
    });
    
    return fields.length > 0 ? fields : this.getDefaultFields();
  }
  
  private mapFieldTypeId(typeId: number): 'string' | 'integer' | 'double' | 'date' | 'boolean' {
    switch (typeId) {
      case 2: return 'integer';
      case 3: case 38: case 39: return 'double';
      case 4: case 5: return 'date';
      case 6: return 'boolean';
      default: return 'string';
    }
  }
  
  private getDefaultFields(): BooleanSearchField[] {
    return [
      { id: 'ContactConfigFields_Forename', label: 'Forename', type: 'string' },
      { id: 'ContactConfigFields_Surname', label: 'Surname', type: 'string' },
      { id: 'ContactConfigFields_AvailabilityDate', label: 'Availability Date', type: 'date' },
      { id: 'ContactConfigFields_CurrentRemuneration', label: 'Current Remuneration', type: 'double' },
      { id: 'ContactConfigFields_DesiredRemuneration', label: 'Desired Remuneration', type: 'double' },
      { id: 'ContactConfigFields_NoticePeriod', label: 'Notice Period', type: 'string' }
    ];
  }
  
  // ============================================================================
  // DataGrid
  // ============================================================================
  
  private async initDataGrid(): Promise<void> {
    const container = document.getElementById('contact-picker-grid-container');
    if (!container) {
      logger.warn('Grid container not found');
      return;
    }
    
    const columns: DataGridColumn[] = [
      { 
        field: 'FullName', 
        header: 'Name', 
        width: '200px',
        sortable: true,
        formatter: (value: string, row: { ContactId: number; ContactType?: string }) => {
          const icon = row.ContactType === 'Candidate' ? 'fas fa-user' : 'fas fa-user-tie';
          return `<span class="font-medium flex items-center gap-2 cursor-pointer hover:text-primary"><i class="${icon} text-primary"></i>${value || '-'}</span>`;
        }
      },
      { 
        field: 'CompanyName', 
        header: 'Company', 
        width: '180px',
        sortable: true,
        formatter: (value: string | null) => value || '<span class="text-base-content/50">-</span>'
      },
      { 
        field: 'JobTitle', 
        header: 'Job Title', 
        width: '150px',
        sortable: true,
        formatter: (value: string | null) => value || '<span class="text-base-content/50">-</span>'
      },
      { 
        field: 'Email', 
        header: 'Email', 
        sortable: true,
        formatter: (value: string | null) => value || '<span class="text-base-content/50">-</span>'
      }
    ];
    
    this.dataGrid = new DataGrid(container, {
      columns,
      localData: [],
      pageSize: this.pageSize,
      selectable: false,
      sortable: true,
      searchable: false,
      mode: 'server',
      totalRecords: 0,
      onPageChange: (page: number, pageSize: number) => {
        this.currentPage = page;
        this.pageSize = pageSize;
        this.loadResults();
      },
      onRowClick: (row: Record<string, unknown>) => {
        this.handleRowSelect(row);
      }
    });
    
    await this.dataGrid.initialize();
    
    // Hide toolbar buttons
    const toolbarButtons = container.querySelector('.join');
    if (toolbarButtons) {
      (toolbarButtons as HTMLElement).style.display = 'none';
    }
    
    logger.debug('DataGrid initialized');
  }
  
  // ============================================================================
  // Data Loading
  // ============================================================================
  
  private async loadResults(): Promise<void> {
    if (!this.dataGrid) {
      logger.warn('Cannot load results - DataGrid not initialized');
      return;
    }
    
    // Use provided apiService or fall back to trisysAPI
    const apiService = this.options.apiService ?? trisysAPI;
    
    try {
      this.dataGrid.showLoading();
      
      const params: { PageNumber: number; RecordsPerPage: number; SearchTreeEnabled?: boolean; SearchTreeJson?: string } = {
        PageNumber: this.currentPage,
        RecordsPerPage: this.pageSize
      };
      
      if (this.searchTree) {
        params.SearchTreeEnabled = true;
        params.SearchTreeJson = JSON.stringify(this.searchTree);
        this.saveLastSearch();
      }
      
      const response = await apiService.searchContactsWithTree(params);
      
      logger.debug('Results loaded', { 
        count: response.List?.length, 
        total: response.TotalRecordCount
      });
      
      let normalizedData = this.normalizeData(response.List || []);
      
      // Filter out excluded contacts
      const excludeIds = this.getExcludeIds();
      if (excludeIds.length > 0) {
        normalizedData = normalizedData.filter(row => 
          !excludeIds.includes(row.ContactId as number)
        );
      }
      
      this.dataGrid.setData({
        records: normalizedData,
        total: response.TotalRecordCount || 0,
        page: this.currentPage
      });
      
      this.dataGrid.hideLoading();
      
    } catch (error) {
      logger.error('Failed to load results', error);
      ToasterService.error('Failed to search contacts');
      this.dataGrid?.hideLoading();
    }
  }
  
  private getExcludeIds(): number[] {
    const excludeIds: number[] = [];
    if (this.options.excludeContactId) {
      excludeIds.push(this.options.excludeContactId);
    }
    if (this.options.excludeContactIds) {
      excludeIds.push(...this.options.excludeContactIds);
    }
    return excludeIds;
  }
  
  private normalizeData(contacts: Record<string, unknown>[]): Record<string, unknown>[] {
    return contacts.map(contact => {
      const normalized: Record<string, unknown> = { ...contact };
      
      const fieldMap: Record<string, string> = {
        'Contact_Christian': 'Forenames',
        'Contact_Surname': 'Surname',
        'Contact_FullName': 'FullName',
        'Contact_Company': 'CompanyName',
        'Contact_CompanyName': 'CompanyName',
        'Contact_JobTitle': 'JobTitle',
        'Contact_ContactType': 'ContactType',
        'Contact_PersonalEMail': 'Email',
        'Contact_WorkEMail': 'Email',
        'Contact_EMail': 'Email',
        'Contact_Id': 'ContactId',
        'Contact_ContactId': 'ContactId'
      };
      
      for (const [apiField, gridField] of Object.entries(fieldMap)) {
        if (contact[apiField] !== undefined && normalized[gridField] === undefined) {
          normalized[gridField] = contact[apiField];
        }
      }
      
      if (!normalized['FullName'] && (normalized['Forenames'] || normalized['Surname'])) {
        const forenames = (normalized['Forenames'] || '') as string;
        const surname = (normalized['Surname'] || '') as string;
        normalized['FullName'] = `${forenames} ${surname}`.trim();
      }
      
      if (!normalized['Email']) {
        normalized['Email'] = contact['PersonalEMail'] || contact['WorkEMail'] || null;
      }
      
      return normalized;
    });
  }
  
  // ============================================================================
  // Selection & Cleanup
  // ============================================================================
  
  private handleRowSelect(row: Record<string, unknown>): void {
    const contactId = row.ContactId as number;
    const fullName = (row.FullName as string) || '';
    
    logger.debug('Contact selected', { contactId, fullName });
    
    if (contactId) {
      this.resolveWith({
        selected: true,
        contactId,
        fullName,
        record: row
      });
    } else {
      logger.warn('Cannot select contact - missing ContactId', row);
    }
  }
  
  private handleCancel(): void {
    logger.debug('Contact picker cancelled');
    this.resolveWith({ selected: false });
  }
  
  private resolveWith(result: ContactPickerResult): void {
    // Capture and clear the resolver first
    const resolver = this.resolvePromise;
    this.resolvePromise = null;
    
    // Close modal (this will trigger cleanup via onClosed)
    if (this.modalId) {
      ModalSystem.hide(this.modalId);
    }
    
    // Resolve promise AFTER modal is closed
    if (resolver) {
      resolver(result);
    }
  }
  
  private cleanup(): void {
    if (this.dataGrid) {
      this.dataGrid.destroy();
      this.dataGrid = null;
    }
    
    if (this.booleanSearch) {
      this.booleanSearch.destroy();
      this.booleanSearch = null;
    }
    
    this.modalId = null;
    this.searchTree = null;
    this.resolvePromise = null;
    
    logger.debug('Contact picker cleaned up');
  }
  
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
